<template>
  <div class="badge-chart-container">
    <div class="chart-wrapper px-4 py-8">
      <div v-if="loading" class="chart-loader">
        <v-progress-circular
          indeterminate
          color="primary"
          size="64"
        ></v-progress-circular>
      </div>
      <div v-if="!loading && !hasData" class="no-data">
        <v-icon size="48" color="grey">mdi-chart-line</v-icon>
        <p class="grey--text">{{ $t('no_data') }}</p>
      </div>
      <canvas v-show="!loading && hasData" ref="chartCanvas"></canvas>
    </div>
    <div class="chart-filters px-4 py-8">
      <v-select
        v-model="selectedBadges"
        :items="badgesWithTotal"
        item-text="value"
        item-value="id"
        :label="$t('badge_name')"
        multiple
        outlined
        dense
        hide-details
        class="my-4"
      ></v-select>

      <v-btn-toggle
        v-model="selectedPeriod"
        mandatory
        dense
        class="period-toggle mb-4"
      >
        <v-btn small value="3" :active-class="'primary'">
          {{ $t('last_3_months') }}
        </v-btn>
        <v-btn small value="6" :active-class="'primary'">
          {{ $t('last_6_months') }}
        </v-btn>
        <v-btn small value="12" :active-class="'primary'">
          {{ $t('last_12_months') }}
        </v-btn>
      </v-btn-toggle>

      <v-btn
        :disabled="!isShowDataEnabled"
        color="primary"
        block
        @click="handleShowData"
      >
        {{ $t('show_data') }}
      </v-btn>
    </div>
  </div>
</template>

<script>
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import chartTrendline from 'chartjs-plugin-trendline';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  CategoryScale,
  Title,
  Tooltip,
  Legend,
  chartTrendline
);

export default {
  name: 'BadgeChartNative',
  props: {
    chartData: {
      type: Object,
      default: () => ({
        labels: [],
        datasets: []
      })
    },
    chartOptions: {
      type: Object,
      default: () => ({})
    },
    badges: {
      type: Array,
      required: true,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      chartInstance: null,
      internalChartData: {
        labels: [],
        datasets: []
      },
      selectedBadges: [],
      selectedPeriod: '3'
    };
  },
  computed: {
    badgesWithTotal() {
      const divider = {
        id: null,
        value: null,
        divider: true
      };
      const totalBadge = {
        id: 0,
        value: this.$t('total_points')
      };
      return [...this.badges, divider, totalBadge];
    },
    isShowDataEnabled() {
      return this.selectedBadges.length > 0 && this.selectedPeriod !== null;
    },
    hasData() {
      return this.internalChartData.datasets.length > 0 &&
             this.internalChartData.labels.length > 0;
    }
  },
  watch: {
    chartData: {
      handler(newData) {
        this.updateInternalData(newData);
        this.updateChart();
      },
      deep: true
    },
    badges: {
      handler(newBadges) {
        if (newBadges?.length > 0 && this.selectedBadges.length === 0) {
          this.selectedBadges = [0];
          this.$nextTick(() => {
            this.handleShowData();
          });
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.updateInternalData(this.chartData);
    if (this.hasData) {
      this.$nextTick(() => {
        this.initChart();
      });
    }
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.destroy();
    }
  },
  methods: {
    handleShowData() {
      if (!this.isShowDataEnabled) return;

      const formatDate = (date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };


      const today = new Date();
      const currentDayOfWeek = today.getDay();
      const endDate = new Date(today);

      const daysUntilSaturday = currentDayOfWeek === 6 ? 0 : 6 - currentDayOfWeek;
      endDate.setDate(today.getDate() + daysUntilSaturday);


      const periodStartDate = new Date(today);
      periodStartDate.setMonth(periodStartDate.getMonth() - parseInt(this.selectedPeriod));

      const startDate = new Date(periodStartDate);
      const dayOfWeek = startDate.getDay();

      startDate.setDate(startDate.getDate() - dayOfWeek);

      const payload = {
        startDate: formatDate(startDate),
        endDate: formatDate(endDate),
        badges: this.selectedBadges
      };
      this.$emit('show-data', payload);
    },
    updateInternalData(data) {
      if (!data || !data.datasets) {
        this.internalChartData = {
          labels: [],
          datasets: []
        };
        return;
      }

      this.internalChartData = {
        labels: data.labels || [],
        datasets: data.datasets || []
      };
    },
    initChart() {
      if (!this.$refs.chartCanvas) {
        return;
      }

      const ctx = this.$refs.chartCanvas.getContext('2d');

      const defaultOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            enabled: true,
            callbacks: {
              label: (context) => {
                const label = context.dataset.label || '';
                const value = context.parsed.y;

                return `${label}: ${value}`;
              }
            }
          }
        },
        scales: {
          x: {
            grid: {
              display: false
            }
          },
          y: {
            grid: {
              display: false
            },
            beginAtZero: true,
            ticks: {
              callback: function(value) {
                if (Number.isInteger(value)) {
                  return value;
                }
              }
            }
          }
        }
      };

      const mergedOptions = this.mergeOptions(defaultOptions, this.chartOptions);

      if (this.chartInstance) {
        this.chartInstance.destroy();
      }

      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.internalChartData.labels,
          datasets: this.internalChartData.datasets
        },
        options: mergedOptions
      });
    },
    updateChart() {
      if (!this.chartInstance) {
        if (this.hasData) {
          this.$nextTick(() => {
            this.initChart();
          });
        }
        return;
      }

      this.chartInstance.data.labels = this.internalChartData.labels;
      this.chartInstance.data.datasets = this.internalChartData.datasets;
      this.chartInstance.update();
    },
    mergeOptions(defaults, custom) {
      const merged = { ...defaults };

      if (custom.scales) {
        merged.scales = {
          ...defaults.scales,
          ...custom.scales
        };
      }

      if (custom.plugins) {
        merged.plugins = {
          ...defaults.plugins,
          ...custom.plugins
        };
      }

      Object.keys(custom).forEach(key => {
        if (key !== 'scales' && key !== 'plugins') {
          merged[key] = custom[key];
        }
      });

      return merged;
    }
  }
};
</script>

<style scoped>
.badge-chart-container {
  display: flex;
  align-items: flex-start;
  gap: 20px;
  width: 100%;
  height: 100%;
}

.chart-wrapper {
  flex: 1;
  min-height: 400px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.chart-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
}

.chart-filters {
  display: flex;
  flex-direction: column;
  min-width: 280px;
  max-width: 320px;
}

.period-toggle {
  width: 100%;
}

.period-toggle >>> .v-btn {
  flex: 1;
  font-size: 11px;
  padding: 0 8px;
}
.v-btn--active.primary {
  color: white;
}
</style>
